import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { Box, Stack, Tooltip, Typography } from '@/primitives';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { FilterInput, LogicalOperator, OperatorOption } from '../../constants';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import BasicSearchValue from './BasicSearchValue';
import {
  PercentileBreedAverageCell,
  PercentileSliderTrack,
  usePercentileSliderModel,
} from './PercentileSlider';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import {
  formatMaxValueLabel,
  formatMinValueLabel,
} from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { slate, white } from '@/lib/colours';
import {
  BREEDPLAN_PERCENTILE_MAX,
  BREEDPLAN_PERCENTILE_MIN,
  buildPercentilePairFilters,
  normalizePercentileInputOnBlur,
  parsePercentileFiltersFromState,
} from '../ebvPercentileFilterHelpers';
import { logError } from '@/lib/logger';

interface Props {
  ebvField: string; // e.g., "ebv1" (lowercase to match GraphQL schema)
  ebvAccField: string; // e.g., "ebv1Acc" (lowercase camelCase to match GraphQL schema)
  label: string; // Display label for the EBV
  /** Society EBV_FIELDS `leftLabel` — low end of trait axis (slider left). */
  ebvLeftLabel?: string | null;
  /** Society EBV_FIELDS `rightLabel` — high end of trait axis (slider right). */
  ebvRightLabel?: string | null;
  /** When true (EBV_FIELDS group Indexes), value placeholders use “Index” instead of “EBV”. */
  isIndexEbv?: boolean;
  /** Society EBV_FIELDS `format` — breed average uses same rules as grid/charts (`$`, decimals, etc.). */
  ebvFormat?: string;
  /** BREEDPLAN percentile slider track/thumb (same palette as EBV charts). */
  percentileTrackColor?: string;
  getUniqueFilters: () => FilterInput[];
  filtersOverride?: FilterInput[];
  setFiltersOverride?: (filters: FilterInput[]) => void;
}

// Derive the percentile field from the EBV field (e.g., "ebv1" -> "ebv1Percentile")
function getPercentileField(ebvField: string): string {
  return `${ebvField}Percentile`;
}

/**
 * Min/max EBV and percentile row inset panels.
 */
function ebvFilterInsetPanelSurfaceStyles(theme: Theme, isDarkMode: boolean) {
  const p = (theme.vars || theme).palette;
  return {
    borderRadius: '8px',
    border: isDarkMode
      ? `1px solid ${alpha(white[50], 0.16)}`
      : `1px solid ${p.divider}`,
    bgcolor: isDarkMode ? alpha(white[50], 0.08) : alpha(slate[900], 0.03),
    px: { xs: 1, md: 1.25 },
    py: { xs: 1, md: 1 },
  };
}

/** Shared outlined + floating label styles; width set per column below. */
function basicSearchEbvOutlinedFieldSharedSx(
  theme: Theme,
  isDarkMode: boolean,
): SystemStyleObject<Theme> {
  return {
    flex: { xs: '1 1 0', sm: '0 0 auto' },
    minWidth: 0,
    '& .MuiInputLabel-root': {
      fontSize: '11px',
      fontWeight: 400,
      color: 'text.secondary',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      minHeight: 40,
      // Light: paper on grey inset so fields don’t look disabled. Dark: transparent like Animal Details tab.
      backgroundColor: isDarkMode
        ? 'transparent'
        : (theme.vars || theme).palette.background.paper,
    },
    '& fieldset': {
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'grey.500',
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
      px: '9px',
      py: '7px',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.25,
    },
  };
}

/** Min/max EBV or Index — narrower to free space for the percentile panel. */
function basicSearchEbvAbsoluteFieldSx(
  theme: Theme,
  isDarkMode: boolean,
): SystemStyleObject<Theme> {
  return {
    ...basicSearchEbvOutlinedFieldSharedSx(theme, isDarkMode),
    width: { xs: 'auto', sm: 88, md: 84 },
    maxWidth: { xs: 'calc(50% - 6px)', sm: 88, md: 84 },
  };
}

/** Min/max % inside tinted panel — slightly wider than EBV for two-digit ranks. */
function basicSearchEbvPercentileFieldSx(
  theme: Theme,
  isDarkMode: boolean,
): SystemStyleObject<Theme> {
  return {
    ...basicSearchEbvOutlinedFieldSharedSx(theme, isDarkMode),
    width: { xs: 'auto', sm: 100, md: 96 },
    maxWidth: { xs: 'calc(50% - 6px)', sm: 100, md: 96 },
  };
}

type EbvAverageData = {
  description?: string;
  [key: string]: string | undefined;
};

export default function BasicSearchEbvCondition({
  ebvField,
  ebvAccField: _ebvAccField,
  label,
  ebvLeftLabel,
  ebvRightLabel,
  isIndexEbv = false,
  ebvFormat,
  percentileTrackColor,
  getUniqueFilters: _getUniqueFilters,
  filtersOverride,
  setFiltersOverride,
}: Props) {
  const searchType = useSearchType();
  const {
    filters: storeFilters,
    setFilters: storeSetFilters,
    updateCommonFilterOperator,
  } = useFilterStore(searchType);
  const society = usePublicSearchContext((context) => context.society);
  const currencySymbol = useCurrencySymbol(society);
  const { ebvLabel, showPercentileSearch } = useEbvDisplayOptions();
  const { isDarkMode } = useResolvedColorScheme();
  const absoluteTextFieldBase = {
    size: 'small' as const,
    sx: (theme: Theme) => basicSearchEbvAbsoluteFieldSx(theme, isDarkMode),
  };
  const percentileTextFieldBase = {
    size: 'small' as const,
    sx: (theme: Theme) => basicSearchEbvPercentileFieldSx(theme, isDarkMode),
  };

  const minEbvFloatLabel = isIndexEbv
    ? 'Min Index'
    : formatMinValueLabel(ebvLabel);
  const maxEbvFloatLabel = isIndexEbv
    ? 'Max Index'
    : formatMaxValueLabel(ebvLabel);
  const minValueAriaLabel = isIndexEbv
    ? 'Minimum index value'
    : `Minimum ${ebvLabel} value`;
  const maxValueAriaLabel = isIndexEbv
    ? 'Maximum index value'
    : `Maximum ${ebvLabel} value`;

  // Use override props if provided, otherwise fall back to store
  const filters = filtersOverride ?? storeFilters;
  const setFilters = setFiltersOverride ?? storeSetFilters;

  // Use the percentile field for min/max percentile filters (not accuracy field)
  const ebvPercentileField = getPercentileField(ebvField);

  // Get EBV average for this specific EBV field
  const ebvAverage = useMemo<string | number | undefined>(() => {
    if (!society.settings || !Array.isArray(society.settings)) return undefined;

    const ebvAverageSetting = society.settings.find(
      (setting) =>
        setting &&
        setting.societySettingType === SOCIETY_SETTING_TYPE.EBV_AVERAGE,
    );

    if (!ebvAverageSetting || !ebvAverageSetting.value) return undefined;

    try {
      const parsed = JSON.parse(ebvAverageSetting.value) as EbvAverageData;
      // Extract field number from ebvField (e.g., "ebv1" -> "1")
      const fieldNumber = ebvField.replace('ebv', '');
      // Get the average value using the key format "ebv_1_avg" or similar
      const avgKey = `ebv_${fieldNumber}_avg`;
      const avgValue = parsed[avgKey];
      if (avgValue === undefined) return undefined;
      // Try to parse as number, if fails return as string
      const num = parseFloat(avgValue);
      return isNaN(num) ? avgValue : num;
    } catch (error) {
      logError('Failed to parse EBV average configuration:', error);
      return undefined;
    }
  }, [society.settings, ebvField]);

  // Get existing filter values
  const minEbvFilter = useMemo(
    () =>
      filters.find(
        (f) =>
          f.field === ebvField &&
          f.operator === OperatorOption.greaterThanOrEqual.value,
      ),
    [filters, ebvField],
  );
  const maxEbvFilter = useMemo(
    () =>
      filters.find(
        (f) =>
          f.field === ebvField &&
          f.operator === OperatorOption.lessThanOrEqual.value,
      ),
    [filters, ebvField],
  );
  const { minStr: minPercValue, maxStr: maxPercValue } = useMemo(
    () => parsePercentileFiltersFromState(filters, ebvPercentileField),
    [filters, ebvPercentileField],
  );

  const minEbvValue = minEbvFilter?.value || '';
  const maxEbvValue = maxEbvFilter?.value || '';

  function getNonEmptyFilters(filters: FilterInput[]) {
    return filters.filter(({ operator, value }) => operator || value);
  }

  // Store latest functions and values in refs to avoid stale closures
  const filtersRef = useRef(filters);
  const setFiltersRef = useRef(setFilters);
  const updateCommonFilterOperatorRef = useRef(updateCommonFilterOperator);

  // Update refs whenever functions or filters change
  useEffect(() => {
    filtersRef.current = filters;
    setFiltersRef.current = setFilters;
    updateCommonFilterOperatorRef.current = updateCommonFilterOperator;
  }, [filters, setFilters, updateCommonFilterOperator]);

  // Direct update function (not debounced - BasicSearchValue handles debouncing)
  // This function works with ALL filters, not just EBV filters, to preserve animal detail filters
  const updateFilter = useCallback(
    (field: string, operator: string, value: string) => {
      // Skip incomplete numeric drafts (e.g. typing "-" before digits); allow negatives.
      const trimmed = value.trim();
      if (trimmed === '-' || trimmed === '-.' || trimmed === '.') return;
      // Work with all filters, not just EBV filters, to preserve other filters
      const allFilters = [...filtersRef.current];

      // Find index of matching filter
      const matchIndex = allFilters.findIndex(
        (f) => f.field === field && f.operator === operator,
      );

      if (value.trim()) {
        if (matchIndex === -1) {
          // No match found - create new filter
          allFilters.push({
            field,
            operator,
            value: value.trim(),
          });
        } else {
          // Update existing filter by creating a new object (immutable update)
          allFilters[matchIndex] = {
            ...allFilters[matchIndex]!,
            value: value.trim(),
          };
        }
      } else {
        // Remove filter if value is empty
        if (matchIndex >= 0) {
          allFilters.splice(matchIndex, 1);
        }
      }

      const nonEmptyFilters = getNonEmptyFilters(allFilters);
      filtersRef.current = nonEmptyFilters;
      setFiltersRef.current(nonEmptyFilters);
      updateCommonFilterOperatorRef.current(LogicalOperator.and);
    },
    [], // Empty deps - uses refs to access latest functions
  );

  const applyPercentileRange = useCallback(
    (nextMin: string, nextMax: string) => {
      const all = [...filtersRef.current];
      const stripped = all.filter(
        (f) =>
          !(
            f.field === ebvPercentileField &&
            (f.operator === OperatorOption.greaterThanOrEqual.value ||
              f.operator === OperatorOption.lessThanOrEqual.value)
          ),
      );
      for (const p of buildPercentilePairFilters(nextMin, nextMax)) {
        stripped.push({
          field: ebvPercentileField,
          operator: p.operator,
          value: p.value,
        });
      }
      const nonEmptyFilters = getNonEmptyFilters(stripped);
      filtersRef.current = nonEmptyFilters;
      setFiltersRef.current(nonEmptyFilters);
      updateCommonFilterOperatorRef.current(LogicalOperator.and);
    },
    [ebvPercentileField],
  );

  /** When EBV min/max gains a value, drop percentile filters for this trait (same edit cycle). */
  const clearTraitPercentileIfEbvHasValue = useCallback(
    (nextMinEbv: string, nextMaxEbv: string) => {
      if (!nextMinEbv.trim() && !nextMaxEbv.trim()) return;
      const { minStr, maxStr } = parsePercentileFiltersFromState(
        filtersRef.current,
        ebvPercentileField,
      );
      if (!minStr.trim() && !maxStr.trim()) return;
      applyPercentileRange('', '');
    },
    [applyPercentileRange, ebvPercentileField],
  );

  /** When percentile min/max is set, drop EBV value range filters for this trait (same edit cycle). */
  const clearTraitEbvRangeIfPercentileSpecified = useCallback(() => {
    const { minStr, maxStr } = parsePercentileFiltersFromState(
      filtersRef.current,
      ebvPercentileField,
    );
    if (!minStr.trim() && !maxStr.trim()) return;
    const all = [...filtersRef.current];
    const stripped = all.filter(
      (f) =>
        !(
          f.field === ebvField &&
          (f.operator === OperatorOption.greaterThanOrEqual.value ||
            f.operator === OperatorOption.lessThanOrEqual.value)
        ),
    );
    const nonEmptyFilters = getNonEmptyFilters(stripped);
    filtersRef.current = nonEmptyFilters;
    setFiltersRef.current(nonEmptyFilters);
    updateCommonFilterOperatorRef.current(LogicalOperator.and);
  }, [ebvField, ebvPercentileField]);

  const handleMinEbvChange = useCallback(
    (value: string) => {
      const prevMax =
        filtersRef.current.find(
          (f) =>
            f.field === ebvField &&
            f.operator === OperatorOption.lessThanOrEqual.value,
        )?.value ?? '';
      updateFilter(ebvField, OperatorOption.greaterThanOrEqual.value, value);
      clearTraitPercentileIfEbvHasValue(value, String(prevMax));
    },
    [ebvField, updateFilter, clearTraitPercentileIfEbvHasValue],
  );

  const handleMaxEbvChange = useCallback(
    (value: string) => {
      const prevMin =
        filtersRef.current.find(
          (f) =>
            f.field === ebvField &&
            f.operator === OperatorOption.greaterThanOrEqual.value,
        )?.value ?? '';
      updateFilter(ebvField, OperatorOption.lessThanOrEqual.value, value);
      clearTraitPercentileIfEbvHasValue(String(prevMin), value);
    },
    [ebvField, updateFilter, clearTraitPercentileIfEbvHasValue],
  );

  const handleMinPercChange = useCallback(
    (value: string) => {
      const { maxStr } = parsePercentileFiltersFromState(
        filtersRef.current,
        ebvPercentileField,
      );
      if (!value.trim() && maxStr.trim()) {
        applyPercentileRange('', '');
      } else {
        applyPercentileRange(value, maxStr);
      }
      clearTraitEbvRangeIfPercentileSpecified();
    },
    [
      applyPercentileRange,
      clearTraitEbvRangeIfPercentileSpecified,
      ebvPercentileField,
    ],
  );

  const handleMaxPercChange = useCallback(
    (value: string) => {
      const { minStr } = parsePercentileFiltersFromState(
        filtersRef.current,
        ebvPercentileField,
      );
      if (value.trim() && !minStr.trim()) {
        return;
      }
      applyPercentileRange(minStr, value);
      clearTraitEbvRangeIfPercentileSpecified();
    },
    [
      applyPercentileRange,
      clearTraitEbvRangeIfPercentileSpecified,
      ebvPercentileField,
    ],
  );

  const maxPercDisabled = !String(minPercValue).trim();

  const percentileModel = usePercentileSliderModel({
    minPercentile: minPercValue,
    maxPercentile: maxPercValue,
    ebvAverage,
    ebvAverageFormat: ebvFormat ?? '0',
    currencySymbol,
    trackColor: percentileTrackColor,
    axisLeftLabel: ebvLeftLabel ?? undefined,
    axisRightLabel: ebvRightLabel ?? undefined,
  });
  const showPercentileBreedAvg =
    percentileModel.formattedAverage !== null &&
    percentileModel.formattedAverage !== '';

  const renderPercentileMinMaxRow = () => (
    <Stack
      direction="row"
      sx={{
        gap: '12px',
        width: { xs: '100%', md: 'auto' },
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      <BasicSearchValue
        floatLabel="Min %"
        textFieldProps={{
          ...percentileTextFieldBase,
          inputProps: {
            min: BREEDPLAN_PERCENTILE_MIN,
            max: BREEDPLAN_PERCENTILE_MAX,
            'aria-label': 'Minimum BREEDPLAN percentile (Min %)',
          },
        }}
        type="number"
        value={minPercValue}
        onChange={handleMinPercChange}
        restrictToNumericChars
        normalizeOnBlur={normalizePercentileInputOnBlur}
      />
      {maxPercDisabled ? (
        <Tooltip title="Set Min % first" placement="top">
          <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' }, minWidth: 0 }}>
            <BasicSearchValue
              floatLabel="Max %"
              textFieldProps={{
                ...percentileTextFieldBase,
                disabled: true,
                inputProps: {
                  min: BREEDPLAN_PERCENTILE_MIN,
                  max: BREEDPLAN_PERCENTILE_MAX,
                  'aria-label': 'Maximum BREEDPLAN percentile (Max %)',
                },
              }}
              type="number"
              value={maxPercValue}
              onChange={handleMaxPercChange}
              restrictToNumericChars
              normalizeOnBlur={normalizePercentileInputOnBlur}
            />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' }, minWidth: 0 }}>
          <BasicSearchValue
            floatLabel="Max %"
            textFieldProps={{
              ...percentileTextFieldBase,
              inputProps: {
                min: BREEDPLAN_PERCENTILE_MIN,
                max: BREEDPLAN_PERCENTILE_MAX,
                'aria-label': 'Maximum BREEDPLAN percentile (Max %)',
              },
            }}
            type="number"
            value={maxPercValue}
            onChange={handleMaxPercChange}
            restrictToNumericChars
            normalizeOnBlur={normalizePercentileInputOnBlur}
          />
        </Box>
      )}
    </Stack>
  );

  return (
    <Box sx={{ mb: { xs: '8px', md: '12px' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: '4px',
          pr: '8px',
          py: { xs: '2px', md: '4px' },
          mb: 0,
          width: { xs: '100%', md: 'fit-content' },
        }}
      >
        <Typography
          variant="titlemedium"
          sx={{
            color: 'text.secondary',
            fontSize: '12px',
            wordBreak: 'break-word',
            lineHeight: 1.25,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Stack
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.25, md: 1.5 },
          pl: '4px',
          alignItems: { xs: 'stretch', md: 'stretch' },
        }}
      >
        <Box
          sx={(theme) => ({
            flex: { md: '0 0 auto' },
            minWidth: 0,
            ...ebvFilterInsetPanelSurfaceStyles(theme, isDarkMode),
          })}
        >
          <Stack
            direction="row"
            sx={{
              gap: '12px',
              width: { xs: '100%', md: 'auto' },
              minWidth: 0,
            }}
          >
            <BasicSearchValue
              floatLabel={minEbvFloatLabel}
              textFieldProps={{
                ...absoluteTextFieldBase,
                inputProps: {
                  'aria-label': minValueAriaLabel,
                },
              }}
              type="number"
              value={minEbvValue}
              onChange={handleMinEbvChange}
              restrictToNumericChars
            />
            <BasicSearchValue
              floatLabel={maxEbvFloatLabel}
              textFieldProps={{
                ...absoluteTextFieldBase,
                inputProps: {
                  'aria-label': maxValueAriaLabel,
                },
              }}
              type="number"
              value={maxEbvValue}
              onChange={handleMaxEbvChange}
              restrictToNumericChars
            />
          </Stack>
        </Box>

        {showPercentileSearch && (
          <>
            <Typography
              variant="labelmedium"
              component="div"
              sx={{
                alignSelf: { xs: 'center', md: 'center' },
                color: 'text.secondary',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                lineHeight: 1,
                pt: { xs: 0.25, md: 1.125 },
                pb: { xs: 0.25, md: 0 },
                px: { xs: 0, md: 0.5 },
                flexShrink: 0,
              }}
            >
              OR
            </Typography>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{
                flex: { md: '1 1 0' },
                minWidth: 0,
                gap: { xs: 1, md: 2 },
                alignItems: { xs: 'stretch', md: 'center' },
              }}
            >
              <Box
                sx={(theme) => ({
                  flex: { md: '1 1 0' },
                  minWidth: 0,
                  ...ebvFilterInsetPanelSurfaceStyles(theme, isDarkMode),
                })}
              >
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {renderPercentileMinMaxRow()}
                  <Box
                    sx={{
                      px: { xs: '4px', md: '4px' },
                      pt: 0,
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <PercentileSliderTrack
                      model={percentileModel}
                      widthMode="fluid"
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: 'auto 1fr',
                    columnGap: 2,
                    alignItems: 'center',
                  }}
                >
                  {renderPercentileMinMaxRow()}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <PercentileSliderTrack
                      model={percentileModel}
                      widthMode="fixed"
                    />
                  </Box>
                </Box>
              </Box>

              {showPercentileBreedAvg ? (
                <PercentileBreedAverageCell
                  formattedAverage={percentileModel.formattedAverage}
                  breedAverageRaw={percentileModel.breedAverageRaw}
                  breedAverageFormat={percentileModel.breedAverageFormat}
                  currencySymbol={currencySymbol}
                  sx={{
                    alignSelf: { xs: 'flex-end', md: 'center' },
                    pr: { xs: '4px', md: 0 },
                  }}
                />
              ) : null}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
